import { CommonModule } from "@angular/common";
import {
  Component,
  ElementRef,
  OnDestroy,
  Renderer2,
  ViewChild,
  computed,
  effect,
  inject,
  input,
} from "@angular/core";
import { LicenseTypes } from "@models/MasterData";
import { GlobalVarsModel } from "@models/ModelGlobalsVar";
import { NewBaseService } from "@providers/newBase.service";
import { Subject, of, switchMap, takeUntil } from "rxjs";

@Component({
  selector: "codere-style-money",
  templateUrl: "./codere-style-money.html",
  styleUrls: ["./codere-style-money.scss"],
  standalone: true,
  imports: [CommonModule],
})
export class CodereStyleMoneyComponent implements OnDestroy {
  @ViewChild("moneySymbol") moneySymbol: ElementRef;
  amount = input.required<string>();
  type = input.required<number>();
  longNumber = computed(() => {
    const hasMoreThanFourDigits = parseFloat(this.amount()) > 9999;
    return hasMoreThanFourDigits;
  });
  valor: string;

  globalVars: GlobalVarsModel;
  newBaseService = inject(NewBaseService);
  renderer = inject(Renderer2);

  private readonly unsubscribe$ = new Subject();

  constructor() {
    this.newBaseService.getVars
      .pipe(
        switchMap((data: GlobalVarsModel) => {
          this.globalVars = data;
          return of(null);
        }),
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe();
    effect(() => {
      this.setView();
    });
  }

  setView() {
    if (this.amount()) {
      switch (this.type()) {
        case typeMoney.freeBet:
          this.freebetMoney();
          break;
        case typeMoney.keyboard:
          this.valor = this.betkeyboardMoney();
          break;
        default:
          this.freebetMoney();
      }
    }
  }

  freebetMoney(): void {
    if (!this.moneySymbol?.nativeElement) return;
    if (this.longNumber()) {
      this.renderer.addClass(this.moneySymbol.nativeElement, "long-number");
    } else {
      this.renderer.removeClass(this.moneySymbol.nativeElement, "long-number");
    }
    if (this.moneySymbol.nativeElement.hasChildNodes()) {
      this.moneySymbol.nativeElement.removeChild(this.moneySymbol.nativeElement.childNodes[0]);
    }
    this.valor = "";
    switch (this.globalVars.licenseType) {
      case LicenseTypes.Mexico:
        this.valor = `<sub class="money-symbol">$</sub>${this.amount()}`;
        break;
      case LicenseTypes.Colombia:
      case LicenseTypes.Panama:
      case LicenseTypes.ArgentinaCaba:
      case LicenseTypes.ArgentinaPBA:
      case LicenseTypes.ArgentinaCordoba:
      case LicenseTypes.ArgentinaMendoza:
        this.valor = `${this.amount()}<sub class="money-symbol">$</sub>`;
        break;
      default:
        this.valor = `${this.amount()}<sub class="money-symbol">&euro;</sub>`;
        break;
    }
    this.moneySymbol?.nativeElement.insertAdjacentHTML("beforeend", this.valor);
  }

  betkeyboardMoney(): string {
    switch (this.globalVars.licenseType) {
      case LicenseTypes.Mexico:
        return `<b>$</b>${this.amount()}`;
      case LicenseTypes.Colombia:
      case LicenseTypes.Panama:
      case LicenseTypes.ArgentinaCaba:
      case LicenseTypes.ArgentinaPBA:
      case LicenseTypes.ArgentinaCordoba:
      case LicenseTypes.ArgentinaMendoza:
        return `${this.amount()}<sub>$</sub>`;
      default:
        return `${this.amount()}<b>&euro;</b>`;
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }
}

enum typeMoney {
  freeBet = 0,
  keyboard,
}
